import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './Header.css'; // Make sure this is imported AFTER your main CSS

const Header = ({
  user = null,
  isAdmin = false,
  isPresident = false,
  isVicePresident = false,
  isSecretary = false,
  isTreasurer = false,
  notifications = [],
  showNotifications = false,
  setShowNotifications = () => {},
  markNotificationAsRead = () => {},
  clearAllNotifications = () => {},
  presidentDelegation = false,
  togglePresidentDelegation = () => {},
  setShowDelegateModal = () => {}
}) => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showDelegationMenu, setShowDelegationMenu] = useState(false);
  const notificationRef = useRef(null);
  const userMenuRef = useRef(null);
  const delegationRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (delegationRef.current && !delegationRef.current.contains(event.target)) {
        setShowDelegationMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setShowNotifications]);

  const handleNotificationClick = (notification) => {
    if (markNotificationAsRead) {
      markNotificationAsRead(notification.id);
    }
    if (notification?.details?.bookingId) {
      navigate(`/booking/${notification.details.bookingId}`);
    } else if (notification?.details?.requestId) {
      navigate(`/booking/${notification.details.requestId}`);
    }
    setShowNotifications(false);
  };

  const unreadCount = (notifications || []).filter(n => !n?.read).length;

  const getInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  const getRoleBadge = () => {
    if (isPresident) return { text: 'President', class: 'president' };
    if (isVicePresident) return { text: 'VP', class: 'vp' };
    if (isSecretary) return { text: 'Sec', class: 'secretary' };
    if (isTreasurer) return { text: 'Treas', class: 'treasurer' };
    if (isAdmin) return { text: 'Admin', class: 'admin' };
    return null;
  };

  const roleBadge = getRoleBadge();

  return (
    <header className="app-header user-panel-header"> {/* Added second class for extra specificity */}
      <div className="header-left">
        <Link to="/" className="logo">
          <img src="/images/logo.png" alt="NLNG RA" className="logo-img" />
          <span className="logo-text">NLNG RA</span>
        </Link>
      </div>

      <div className="header-right">
        {/* Delegation Indicator for President */}
        {isPresident && (
          <div className="delegation-wrapper" ref={delegationRef}>
            <button 
              className={`delegation-indicator ${presidentDelegation ? 'active' : ''}`}
              onClick={() => setShowDelegationMenu(!showDelegationMenu)}
            >
              <i className="fa-solid fa-user-check"></i>
              <span className="delegation-text">
                {presidentDelegation ? 'Delegation Active' : 'Delegate'}
              </span>
            </button>

            <AnimatePresence>
              {showDelegationMenu && (
                <motion.div 
                  className="delegation-menu"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <div className="delegation-menu-header">
                    <h4>Delegation Control</h4>
                  </div>
                  <div className="delegation-menu-body">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={presidentDelegation}
                        onChange={togglePresidentDelegation}
                      />
                      <span className="toggle-slider"></span>
                      <span className="toggle-label">
                        {presidentDelegation ? 'Delegation ON' : 'Delegation OFF'}
                      </span>
                    </label>
                    <p className="delegation-info-text">
                      When active, Vice President can approve bookings
                    </p>
                    <button 
                      className="delegate-users-btn"
                      onClick={() => {
                        if (setShowDelegateModal) setShowDelegateModal(true);
                        setShowDelegationMenu(false);
                      }}
                    >
                      <i className="fa-solid fa-user-plus"></i>
                      Manage Delegated Users
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Notifications */}
        <div className="notifications-wrapper" ref={notificationRef}>
          <button 
            className="notifications-btn"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <i className="fa-solid fa-bell"></i>
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount}</span>
            )}
          </button>

          <AnimatePresence>
            {showNotifications && (
              <motion.div 
                className="notifications-dropdown"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <div className="notifications-header">
                  <h3>Notifications</h3>
                  {(notifications || []).length > 0 && (
                    <button 
                      className="clear-all-btn"
                      onClick={clearAllNotifications}
                    >
                      Clear All
                    </button>
                  )}
                </div>

                <div className="notifications-list">
                  {(notifications || []).length > 0 ? (
                    (notifications || []).map(notification => (
                      <div 
                        key={notification?.id}
                        className={`notification-item ${!notification?.read ? 'unread' : ''} ${notification?.type || ''}`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="notification-icon">
                          {notification?.type === 'success' && <i className="fa-solid fa-check-circle"></i>}
                          {notification?.type === 'warning' && <i className="fa-solid fa-exclamation-triangle"></i>}
                          {notification?.type === 'info' && <i className="fa-solid fa-info-circle"></i>}
                          {notification?.type === 'error' && <i className="fa-solid fa-times-circle"></i>}
                        </div>
                        <div className="notification-content">
                          <p className="notification-message">{notification?.message}</p>
                          <span className="notification-time">
                            {notification?.timestamp ? new Date(notification.timestamp).toLocaleString() : ''}
                          </span>
                        </div>
                        {!notification?.read && <span className="unread-dot"></span>}
                      </div>
                    ))
                  ) : (
                    <div className="no-notifications">
                      <i className="fa-solid fa-bell-slash"></i>
                      <p>No notifications</p>
                    </div>
                  )}
                </div>

                {(notifications || []).length > 0 && (
                  <div className="notifications-footer">
                    <Link to="/notifications" onClick={() => setShowNotifications(false)}>
                      View All
                    </Link>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* User Menu */}
        <div className="user-menu-wrapper" ref={userMenuRef}>
          <button 
            className="user-menu-btn"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            <div className="user-avatar">
              {user?.profilePicture ? (
                <img src={user.profilePicture} alt={user?.first_name} />
              ) : (
                <span className="avatar-initials">{getInitials()}</span>
              )}
            </div>
            <div className="user-info">
              <span className="user-name">
                {user?.first_name || 'Member'}
                {roleBadge && (
                  <span className={`role-badge ${roleBadge.class}`}>
                    {roleBadge.text}
                  </span>
                )}
              </span>
              <span className="user-email">{user?.email}</span>
            </div>
            <i className="fa-solid fa-chevron-down"></i>
          </button>

          <AnimatePresence>
            {showUserMenu && (
              <motion.div 
                className="user-dropdown"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <div className="dropdown-header">
                  <div className="dropdown-user-info">
                    <strong>{user?.first_name} {user?.last_name}</strong>
                    <span>{user?.email}</span>
                  </div>
                </div>

                <div className="dropdown-menu">
                  <Link to="/profile" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                    <i className="fa-solid fa-user"></i>
                    <span>My Profile</span>
                  </Link>
                  <Link to="/my-bookings" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                    <i className="fa-solid fa-calendar"></i>
                    <span>My Bookings</span>
                  </Link>
                  <Link to="/notifications" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                    <i className="fa-solid fa-bell"></i>
                    <span>Notifications</span>
                    {unreadCount > 0 && (
                      <span className="menu-badge">{unreadCount}</span>
                    )}
                  </Link>
                  <Link to="/settings" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                    <i className="fa-solid fa-gear"></i>
                    <span>Settings</span>
                  </Link>

                  {(isAdmin || isPresident) && (
                    <>
                      <div className="dropdown-divider"></div>
                      <Link to="/admin" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                        <i className="fa-solid fa-shield"></i>
                        <span>Admin Dashboard</span>
                      </Link>
                    </>
                  )}

                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item logout" onClick={() => {
                    // Clear auth data
                    localStorage.removeItem('nlngra_user');
                    localStorage.removeItem('nlngra_token');
                    localStorage.removeItem('nlngra_role');
                    localStorage.removeItem('nlngra_permissions');
                    
                    // Redirect to login
                    window.location.href = '/login';
                  }}>
                    <i className="fa-solid fa-sign-out-alt"></i>
                    <span>Logout</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
};

export default Header;